// The logo as it sits on a dark background: its darkest colours turned white.
//
// The PAMH mark draws its letters and top figure in the same navy as the side pane and the sign-in
// panel, so on those they vanish. It used to sit on a white tile or disc there; the owner asked for
// the logo to sit straight on whatever is behind it (2026-10-02). This is the usual answer, a
// reversed logo: every pixel darker than RELATIVE luminance 0.12 (the logo navy is about 0.03; its
// green, gold and sky blue are all well above) becomes white with the same transparency, so the
// edges stay smooth. It is worked out from the image itself, so a different logo uploaded in
// Settings > Branding is reversed the same way, and it suits the Green theme's dark green too.
//
// Done once per image in a canvas and cached as a data URL. Returns null when the image cannot be
// read back (a cross-origin image without CORS taints the canvas), so the caller can fall back.

const DARK = 0.12;
const cache = new Map();

const channel = (c) => {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};
const luminance = (r, g, b) => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);

/** Turn the dark pixels of an RGBA buffer white, in place. Exported for testing. */
export const reverseDarkPixels = (data) => {
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] && luminance(data[i], data[i + 1], data[i + 2]) < DARK) {
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
    }
  }
  return data;
};

/** A data URL of the logo at `src` reversed for a dark background, or null if it cannot be made. */
export const logoOnDark = (src) => {
  if (!src) return Promise.resolve(null);
  if (cache.has(src)) return cache.get(src);
  const job = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
        reverseDarkPixels(pixels.data);
        ctx.putImageData(pixels, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
  cache.set(src, job);
  return job;
};
